import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static values = {
    scrollSpeed: { type: Number, default: 2000 },
    waitTime: { type: Number, default: 5000 }
  };

  connect() {
    this.isConnected = true;
    this.boundUpdateFade = this.updateFade.bind(this);
    this.element.addEventListener('scroll', this.boundUpdateFade);
    this.updateFade();
    this.checkAndScroll();
  }

  checkAndScroll() {
    if (!this.isConnected) return;

    const scrollHeight = this.element.scrollHeight;
    const clientHeight = this.element.clientHeight;

    if (scrollHeight <= clientHeight) {
      // Instead of cancelling, retry the waitTime
      setTimeout(() => {
        if (this.isConnected) {
          this.checkAndScroll();
        }
      }, this.waitTimeValue);
      return;
    }

    const overflowHeight = scrollHeight - clientHeight;
    const itemHeight = this.getAverageItemHeight();
    const overflowingItems = Math.ceil(overflowHeight / itemHeight);
    const scrollDuration = overflowingItems * this.scrollSpeedValue;

    this.animateScroll(overflowHeight, scrollDuration);
  }

  getAverageItemHeight() {
    const items = this.element.children;
    if (items.length === 0) return 0;

    const firstItem = items[0];
    const computedStyle = window.getComputedStyle(firstItem);
    const itemHeight = firstItem.offsetHeight;
    const gap = parseFloat(computedStyle.marginBottom) || 0;

    return itemHeight + gap;
  }

  updateFade() {
    const containerRect = this.element.getBoundingClientRect();
    const containerTop = containerRect.top;
    const containerBottom = containerRect.bottom;
    const fadeDistance = 5; // Distance in pixels for fade effect

    Array.from(this.element.children).forEach((item) => {
      const itemRect = item.getBoundingClientRect();
      const itemTop = itemRect.top;
      const itemBottom = itemRect.bottom;

      let opacity = 1;

      // Fade out at top
      if (itemTop < containerTop) {
        const overflowAmount = containerTop - itemTop;
        const fadePercentage = Math.min(overflowAmount / fadeDistance, 1);
        opacity = Math.min(opacity, 1 - fadePercentage);
      }

      // Fade out at bottom
      if (itemBottom > containerBottom) {
        const overflowAmount = itemBottom - containerBottom;
        const fadePercentage = Math.min(overflowAmount / fadeDistance, 1);
        opacity = Math.min(opacity, 1 - fadePercentage);
      }

      item.style.opacity = opacity;
    });
  }

  async animateScroll(distance, duration) {
    if (!this.isConnected) return;

    await this.wait(this.waitTimeValue);
    if (!this.isConnected) return;

    await this.smoothScroll(distance, duration);
    if (!this.isConnected) return;

    await this.wait(this.waitTimeValue);
    if (!this.isConnected) return;

    await this.smoothScroll(0, duration);
    if (!this.isConnected) return;

    this.checkAndScroll();
  }

  smoothScroll(targetScrollTop, duration) {
    return new Promise((resolve) => {
      const startScrollTop = this.element.scrollTop;
      const distance = targetScrollTop - startScrollTop;
      const startTime = performance.now();

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Linear animation - no easing
        this.element.scrollTop = startScrollTop + distance * progress;
        this.updateFade();

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animateScroll);
    });
  }

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  disconnect() {
    this.isConnected = false;
    this.element.removeEventListener('scroll', this.boundUpdateFade);
  }
}